import React, { useMemo, useState } from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  Textarea,
  Select,
  Spinner,
} from "@chakra-ui/react";
import {
  useAdmissionNotes,
  useAddAdmissionNote,
  type AdmissionNote,
} from "../../apis/patients";

type Props = {
  admissionId: string;
};

const fmt = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

export default function AdmissionNotesPanel({ admissionId }: Props) {
  const {
    data: notes = [],
    isLoading,
    isError,
  } = useAdmissionNotes(admissionId);

  const { mutate: addNote, isPending: isSaving } =
    useAddAdmissionNote(admissionId);

  const [body, setBody] = useState("");
  const [role, setRole] = useState<string>("nurse"); // 👈 default nurse for this panel

  const sortedNotes: AdmissionNote[] = useMemo(() => {
    if (!Array.isArray(notes)) return [];
    return [...notes].sort((a, b) => {
      const pa = a.pinned ? 1 : 0;
      const pb = b.pinned ? 1 : 0;
      if (pa !== pb) return pb - pa;
      const ta = new Date(a.createdAt || "").getTime();
      const tb = new Date(b.createdAt || "").getTime();
      return tb - ta;
    });
  }, [notes]);

  const handleAdd = () => {
    const trimmed = body.trim();
    if (!trimmed) return;

    addNote(
      {
        body: trimmed,
        authorRole: role || "nurse",
        pinned: false,
      },
      {
        onSuccess: () => {
          setBody("");
        },
      },
    );
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={3} bg="white">
      {/* Existing notes list */}
      {isLoading ? (
        <Spinner size="sm" />
      ) : isError ? (
        <Text fontSize="sm" color="red.500">
          Failed to load admission notes.
        </Text>
      ) : sortedNotes.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No notes recorded yet.
        </Text>
      ) : (
        <VStack align="stretch" spacing={3} mb={3}>
          {sortedNotes.map((n) => (
            <Box
              key={n._id}
              p={2}
              borderWidth="1px"
              borderRadius="md"
              bg={n.pinned ? "yellow.50" : "gray.50"}
            >
              <HStack justify="space-between" mb={1}>
                <HStack spacing={2}>
                  <Badge>
                    {(n.authorRole || "staff").toString().toUpperCase()}
                  </Badge>
                  {n.authorName && (
                    <Text fontSize="xs" color="gray.700">
                      {n.authorName}
                    </Text>
                  )}
                  {n.pinned && (
                    <Badge colorScheme="yellow" variant="solid">
                      PINNED
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  {fmt(n.createdAt)}
                </Text>
              </HStack>
              <Text fontSize="sm" whiteSpace="pre-wrap">
                {n.body}
              </Text>
            </Box>
          ))}
        </VStack>
      )}

      {/* Add new note */}
      <Box mt={3}>
        <HStack mb={2}>
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            size="sm"
            maxW="160px"
          >
            <option value="nurse">Nurse</option>
            <option value="doctor">Doctor</option>
            <option value="pharmacist">Pharmacist</option>
            <option value="nutritionist">Nutritionist</option>
            <option value="lab">Lab</option>
            <option value="other">Other</option>
          </Select>
          <Text fontSize="xs" color="gray.500">
            New note role
          </Text>
        </HStack>
        <Textarea
          placeholder="Write a new admission note..."
          size="sm"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <HStack mt={2} justify="flex-end">
          <Button
            size="sm"
            colorScheme="teal"
            onClick={handleAdd}
            isLoading={isSaving}
            isDisabled={isSaving || !body.trim()}
          >
            Add Note
          </Button>
        </HStack>
      </Box>
    </Box>
  );
}
